/* components/patterns/RoleEditorModal.jsx — создание/правка роли доступа (T3).
   Название + матрица доступов аналитики: группы = вкладки ACCESS_TABS (тумблер вкладки),
   под включённой вкладкой — чекбоксы её секций ACCESS_SECTIONS; выключенная вкладка
   схлопывает секции. Новая роль — всё включено. Сохранение шлёт grants ТОЛЬКО из снятых
   галок (false-ключи; сборка — matrixToGrants из adapters/settings.js, там же тесты).
   Данные сверху: onSave(role|null, {name, grants}) даёт TeamTab (модалка сама не фетчит).
   Полноэкранная на xs (Modal sheet), матрица юзабельна на 380px. */

import React from 'react';
import { Button } from '../ui/Button.jsx';
import { Checkbox, Field, TextInput } from '../ui/Field.jsx';
import { Modal } from '../ui/Modal.jsx';
import { Switch } from '../ui/Switch.jsx';
import { ACCESS_SECTIONS, ACCESS_TABS } from '../../lib/analyticsAccess.js';
import { grantsToMatrix, matrixToGrants } from '../../lib/adapters/settings.js';

  const { useState, useEffect } = React;

  const tabBoxStyle = {
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-md)',
    padding: 'var(--space-10) var(--space-12)',
  };

  // role=null → создание (всё включено); role → правка (матрица из role.grants).
  function RoleEditorModal({ open, role, onClose, onSave }) {
    const [name, setName] = useState('');
    const [matrix, setMatrix] = useState(() => grantsToMatrix(null));
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    useEffect(() => {
      if (!open) return;
      setName(role ? role.name : '');
      setMatrix(grantsToMatrix(role ? role.grants : null));
      setError(null);
      setBusy(false);
    }, [open, role]);

    const toggleTab = (tabKey, on) => {
      setMatrix((m) => ({ ...m, tabs: { ...m.tabs, [tabKey]: on } }));
    };
    const toggleSection = (tabKey, secKey, on) => {
      setMatrix((m) => ({
        ...m,
        sections: { ...m.sections, [tabKey]: { ...m.sections[tabKey], [secKey]: on } },
      }));
    };

    const submit = async () => {
      if (busy || !name.trim()) return;
      setError(null); setBusy(true);
      try {
        await Promise.resolve(onSave && onSave(role || null, { name: name.trim(), grants: matrixToGrants(matrix) }));
        onClose && onClose();
      } catch (e) {
        setError(e.code === 'invalid_name' ? 'Укажите корректное название роли'
          : e.code === 'invalid_grants' ? 'Некорректная матрица доступов — обновите страницу и попробуйте снова'
          : (e.detail || e.message || 'Не удалось сохранить роль'));
        setBusy(false);
      }
    };

    return (
      <Modal open={open} onClose={onClose} title={role ? 'Изменить роль' : 'Новая роль'} size="md"
        footer={<>
          <Button variant="ghost" onClick={onClose} disabled={busy}>Отмена</Button>
          <Button variant="primary" iconLeft="check" loading={busy} disabled={busy || !name.trim()} onClick={submit}>
            {role ? 'Сохранить' : 'Создать роль'}
          </Button>
        </>}>
        <div className="stack stack-16">
          <Field label="Название" required error={error}
            hint="Роль назначается участникам команды и ограничивает их доступ к аналитике">
            <TextInput value={name} invalid={!!error} placeholder="Например, Бармен"
              onChange={(e) => { setName(e.target.value); setError(null); }}
              onKeyDown={(e) => { if (e.key === 'Enter' && name.trim()) submit(); }} autoFocus />
          </Field>

          <div className="stack stack-8">
            <span className="t-label">Доступ к аналитике</span>
            <span className="t-caption text-mute">
              Выключите вкладку целиком или снимите галки с отдельных её разделов.
              Всё, что включено, остаётся доступным.
            </span>
            {ACCESS_TABS.map((t) => {
              const tabOn = matrix.tabs[t.key] !== false;
              const secs = ACCESS_SECTIONS[t.key] || [];
              return (
                <div key={t.key} className="stack stack-8" style={tabBoxStyle}>
                  <div className="cluster cluster-8" style={{ justifyContent: 'space-between', flexWrap: 'nowrap' }}>
                    <span className="t-label">{t.label}</span>
                    <Switch checked={tabOn} ariaLabel={'Вкладка ' + t.label}
                      onChange={(on) => toggleTab(t.key, on)} />
                  </div>
                  {tabOn && secs.length > 0 && (
                    <div className="stack stack-6">
                      {secs.map((s) => (
                        <Checkbox key={s.key} label={s.label}
                          checked={matrix.sections[t.key][s.key] !== false}
                          onChange={(e) => toggleSection(t.key, s.key, e.target.checked)} />
                      ))}
                    </div>
                  )}
                  {!tabOn && (
                    <span className="t-caption text-mute">Вкладка скрыта для этой роли{secs.length ? ' вместе со всеми разделами' : ''}</span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </Modal>
    );
  }

export { RoleEditorModal };
